import React, { forwardRef, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { useTheme } from "@mui/material/styles";
import type { Appearance } from "@stripe/stripe-js";
import { grayMedium, granite } from "design-system/colors";
import { STRIPE_PROMISE } from "providers/StripeElementsProvider/stripe";
import { useOperator } from "globals/hooks/useOperator";
import SetupElement from "./SetupElement";
import SavedCardSession from "./SavedCardSession";
import { getSetupKey, readSetupKey, retainSetupKey } from "./setupAttemptKey";
import type {
  PaymentMethodSetupHandle,
  PaymentMethodSetupProps,
} from "./types";

const SetupSession = forwardRef<
  PaymentMethodSetupHandle,
  PaymentMethodSetupProps & { storageScope: string; onBehalfOf?: string }
>((props, ref) => {
  const theme = useTheme();
  const [stripePromise] = useState(() => STRIPE_PROMISE.catch(() => null));
  const [{ attemptKey, reusedAttempt }] = useState(() => {
    const stored = !props.attemptGeneration && readSetupKey(props.storageScope);
    const key =
      props.attemptKey ||
      getSetupKey(props.storageScope, !!props.attemptGeneration);
    if (props.attemptKey && stored !== key)
      retainSetupKey(props.storageScope, key);
    return { attemptKey: key, reusedAttempt: stored === key };
  });
  const [retryCount, setRetryCount] = useState(0);
  const appearance: Appearance = {
    theme: "stripe",
    labels: "above",
    variables: {
      fontFamily: theme.typography.fontFamily,
      fontSizeBase: "16px",
      colorPrimary: theme.palette.primary.main,
      colorText: theme.palette.text.primary,
      colorBackground: theme.palette.background.paper,
      colorDanger: theme.palette.error.main,
      borderRadius: "4px",
      spacingUnit: "4px",
    },
    rules: {
      ".Input": {
        border: `1px solid ${grayMedium}`,
        boxShadow: "none",
        padding: "16px 12px",
      },
      ".Input:focus": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
      },
      ".Label": { color: granite, fontSize: "14px", fontWeight: "500" },
    },
  };
  if (props.savedPaymentMethods && !props.resume) {
    return (
      <SavedCardSession
        {...props}
        key={retryCount}
        ref={ref}
        attemptKey={attemptKey}
        appearance={appearance}
        stripePromise={stripePromise}
        onRetry={() => setRetryCount((count) => count + 1)}
      />
    );
  }
  return (
    <Elements
      key={retryCount}
      stripe={stripePromise}
      options={{
        mode: "setup",
        currency: props.currency.toLowerCase(),
        onBehalfOf: props.onBehalfOf,
        paymentMethodTypes: ["card"],
        appearance,
        fonts: [
          {
            cssSrc:
              "https://fonts.googleapis.com/css?family=Poppins:300,300i,400,500,600",
          },
        ],
      }}
    >
      <SetupElement
        {...props}
        ref={ref}
        attemptKey={attemptKey}
        stripePromise={stripePromise}
        checkStatusBeforeConfirm={reusedAttempt || retryCount > 0}
        onRetry={() => setRetryCount((count) => count + 1)}
      />
    </Elements>
  );
});

const PaymentMethodSetup = forwardRef<
  PaymentMethodSetupHandle,
  PaymentMethodSetupProps
>((props, ref) => {
  const { context } = props;
  const { operator } = useOperator();
  const onBehalfOf = operator.stripeAccountId || undefined;
  const storageScope = JSON.stringify([
    props.operatorId,
    context.payerId,
    context.surface,
    context.requestId,
    context.invoiceId,
    context.checkoutContext,
    props.attemptScope,
  ]);
  return (
    <SetupSession
      key={JSON.stringify([
        storageScope,
        props.attemptKey,
        props.attemptGeneration,
        props.resume?.setupIntentId,
        props.currency,
        onBehalfOf,
      ])}
      {...props}
      storageScope={storageScope}
      onBehalfOf={onBehalfOf}
      ref={ref}
    />
  );
});

SetupSession.displayName = "SetupSession";
PaymentMethodSetup.displayName = "PaymentMethodSetup";
export default PaymentMethodSetup;
export type {
  PaymentMethodSetupHandle,
  PaymentMethodSetupProps,
  PaymentMethodSetupState,
  SavedPaymentMethod,
} from "./types";
